import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import jsPDF from 'jspdf';
import { 
  DocumentTextIcon, 
  ArrowPathIcon, 
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface AIReport {
  mainReport: string;
  detailedInsights?: string;
  generatedAt: string;
  dataSource: {
    filename: string;
    period: string;
    monthsAnalyzed: number;
  };
}

interface ReportGeneratorProps {
  analysisData: any;
  onBackToAnalysis: () => void;
  onNewAnalysis: () => void;
  existingReport?: AIReport | null;
  onReportGenerated?: (report: AIReport) => void;
}

const AIReportGenerator: React.FC<ReportGeneratorProps> = ({ 
  analysisData, 
  onBackToAnalysis, 
  onNewAnalysis,
  existingReport,
  onReportGenerated
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<string>('');
  const [reportSource, setReportSource] = useState<'cache' | 'generated' | null>(null);
  
  // Utiliser useRef pour garantir une seule exécution même en cas de double montage
  const hasGeneratedRef = useRef(false);
  const initializationRef = useRef(false);

  useEffect(() => {
    // Protection absolue contre les doubles exécutions
    if (initializationRef.current) {
      console.log('⚠️ Tentative de re-initialisation bloquée');
      return;
    }
    
    initializationRef.current = true;
    
    if (existingReport) {
      setReport(existingReport.mainReport);
      setReportSource('cache');
      
      console.log('📄 Rapport IA chargé depuis le cache (unique)');
      toast.success('Rapport chargé depuis le cache !', {
        duration: 2000,
        icon: '⚡',
      });
    } else if (!hasGeneratedRef.current) {
      // Générer une seule fois avec protection par ref
      console.log('🔄 Génération initiale du rapport (protection ref)');
      hasGeneratedRef.current = true;
      generateReport();
    }
  }, [existingReport]);

  const generateReport = useCallback(async (forceRegenerate = false) => {
    // Triple protection contre les appels multiples
    if (isGenerating) {
      console.log('⚠️ Génération déjà en cours, abandon');
      return;
    }
    
    if (!forceRegenerate && hasGeneratedRef.current && report) {
      console.log('📄 Rapport déjà généré, pas de régénération');
      return;
    }
    
    if (!forceRegenerate && report) {
      console.log('📄 Rapport déjà existant, pas de régénération');
      return;
    }

    // Marquer comme en cours de génération
    if (!forceRegenerate) {
      hasGeneratedRef.current = true;
    }

    console.log('🚀 DÉBUT génération rapport UNIQUE, forceRegenerate:', forceRegenerate);
    setIsGenerating(true);
    
    // Validation des données avant envoi
    if (!analysisData || !analysisData.analysis || !analysisData.analysis.kpis) {
      console.error('❌ Données d\'analyse manquantes ou incomplètes');
      const fallbackReport = generateFallbackReport();
      setReport(fallbackReport);
      setReportSource('generated');
      
      toast.error('Données incomplètes - Rapport généré en mode local', {
        duration: 4000,
        icon: '⚠️',
      });
      setIsGenerating(false);
      return;
    }
    
    try {
      console.log('🚀 Envoi données vers API:', {
        hasAnalysisData: !!analysisData,
        hasKpis: !!analysisData?.analysis?.kpis,
        yearCurrent: analysisData.analysis.kpis.yearCurrent,
        yearPrevious: analysisData.analysis.kpis.yearPrevious
      });

      const bodyData = {
        analysisData: analysisData.analysis,
        regenerate: forceRegenerate
      };
      
      console.log('📦 Corps de la requête:', {
        bodyData,
        hasAnalysisDataInBody: !!bodyData.analysisData,
        hasKpisInBody: !!bodyData.analysisData?.kpis,
        bodyStringified: JSON.stringify(bodyData, null, 2).substring(0, 500) + '...'
      });

      const response = await fetch('/api/generate-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Erreur API:', errorData);
        
        // Si erreur 400 (données manquantes), utiliser le fallback local
        if (response.status === 400) {
          console.log('🔄 Utilisation du fallback local suite à erreur 400');
          const fallbackReport = generateFallbackReport();
          setReport(fallbackReport);
          setReportSource('generated');
          
          toast.error('Rapport généré en mode local (données incomplètes)', {
            duration: 4000,
            icon: '⚠️',
          });
          return;
        }
        
        throw new Error(errorData.error || 'Erreur lors de la génération du rapport');
      }

      const data = await response.json();
      
      setReport(data.report);
      setReportSource('generated');

      const aiReport: AIReport = {
        mainReport: data.report,
        generatedAt: new Date().toISOString(),
        dataSource: {
          filename: 'Données analysées',
          period: `${analysisData.analysis.kpis.currentMonthName} ${analysisData.analysis.kpis.yearCurrent}`,
          monthsAnalyzed: analysisData.analysis.kpis.currentMonth
        }
      };

      if (onReportGenerated) {
        onReportGenerated(aiReport);
      }

      // Déterminer le message selon le type de génération
      if (data.isAIGenerated) {
        toast.success(forceRegenerate ? 'Rapport IA régénéré avec succès !' : 'Rapport IA généré avec succès !', {
          duration: 4000,
          icon: '🤖',
        });
      } else {
        toast.error(forceRegenerate ? 'Rapport fallback régénéré' : 'Rapport généré en mode fallback', {
          duration: 4000,
          icon: '🔄',
        });
      }

    } catch (error) {
      console.error('❌ Erreur génération rapport:', error);
      
      // En cas d'erreur réseau ou autre, utiliser le fallback local
      console.log('🔄 Utilisation du fallback local suite à erreur');
      const fallbackReport = generateFallbackReport();
      setReport(fallbackReport);
      setReportSource('generated');
      
      toast.error('Erreur réseau - Rapport généré en mode local', {
        duration: 6000,
        icon: '⚠️',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [analysisData, onReportGenerated]);

  const generateFallbackReport = useCallback(() => {
    const kpis = analysisData.analysis.kpis;
    
    return `# 📊 Rapport d'Analyse Financière

## Synthèse Exécutive

### Performance ${kpis.yearCurrent} (Année N)
- **Chiffre d'affaires à date** : **${kpis.totalRevenueToDateCurrent}**
- **Fréquentation à date** : **${kpis.totalFreqToDateCurrent}** visiteurs
- **Panier moyen à date** : **${kpis.avgPMToDateCurrent}**

### Comparaison avec ${kpis.yearPrevious} (Année N-1)
- **Évolution CA** : ${kpis.revenueGrowthToDate}
- **Évolution Fréquentation** : ${kpis.freqGrowthToDate}
- **Évolution Panier Moyen** : ${kpis.pmGrowthToDate}

### Objectifs ${kpis.yearCurrent}
- **Réalisation objectif CA** : ${kpis.objectiveRealizationToDate}
- **CA Objectif annuel** : **${kpis.totalCAObjectivesAnnual}**

## Points Clés

🚀 **Forces identifiées :**
- Données financières structurées et complètes
- Suivi mensuel régulier des performances
- Objectifs définis pour l'année en cours

📈 **Opportunités d'amélioration :**
- Analyse approfondie des tendances saisonnières
- Optimisation du panier moyen selon les périodes
- Suivi renforcé des écarts par rapport aux objectifs

⚠️ **Points d'attention :**
- Surveillance continue des indicateurs clés
- Ajustement des stratégies selon les performances
- Anticipation des variations saisonnières

---
*Rapport généré automatiquement - ${new Date().toLocaleDateString('fr-FR')}*`;
  }, [analysisData]);

  const copyToClipboard = useCallback(async () => {
    const content = report;
    try {
      await navigator.clipboard.writeText(content);
      toast.success(`Rapport copié dans le presse-papiers !`, {
        duration: 2000,
        icon: '📋',
      });
    } catch (error) {
      toast.error('Erreur lors de la copie', {
        duration: 2000,
      });
    }
  }, [report]);

  const exportToPDF = useCallback(async () => {
    try {
      const { jsPDF } = await import('jspdf');
      
      // Fonction pour formater les nombres avec des espaces comme séparateurs
      const formatNumber = (num: string | number): string => {
        const numValue = typeof num === 'string' ? parseFloat(num) : num;
        if (isNaN(numValue)) return '0';
        return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      };
      
      // Créer le PDF en format A4
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      
      // Fonction pour ajouter l'en-tête avec logo sur chaque page
      const addHeader = (pageNumber: number, totalPages: number) => {
        // Fond en-tête noir
        pdf.setFillColor(26, 26, 26);
        pdf.rect(0, 0, pageWidth, 25, 'F');
        
        // Ligne accent orange
        pdf.setFillColor(217, 119, 6);
        pdf.rect(0, 25, pageWidth, 2, 'F');
        
        // Ajouter le logo (si disponible)
        try {
          // Vous devez placer votre logo dans le dossier public/logo-paries.png
          const logoImg = new Image();
          logoImg.src = '/logo-paries.png'; // Chemin vers votre logo
          
          // Ajouter le logo avec proportions correctes
          pdf.addImage(logoImg, 'PNG', margin, 3, 30, 18);
          
        } catch (error) {
          // Fallback si le logo n'est pas disponible
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text('PARIÈS', margin, 17);
          
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.text('GOURMANDISES BASQUES', margin + 25, 17);
        }
        
        // Titre en-tête
        pdf.setFontSize(12);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont('helvetica', 'bold');
        pdf.text('RAPPORT D\'ANALYSE FINANCIÈRE IA', margin + 40, 12);
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Analyse intelligente par OpenAI GPT-4o', margin + 40, 18);
        
        // Numéro de page
        pdf.setFontSize(8);
        pdf.setTextColor(200, 200, 200);
        pdf.text(`Page ${pageNumber}/${totalPages}`, pageWidth - margin - 20, 17);
        
        // Reset couleur texte
        pdf.setTextColor(26, 26, 26);
      };
      
      // Fonction pour ajouter le pied de page
      const addFooter = () => {
        const footerY = pageHeight - 15;
        pdf.setFillColor(249, 250, 251);
        pdf.rect(0, footerY - 5, pageWidth, 20, 'F');
        
        pdf.setFontSize(8);
        pdf.setTextColor(107, 114, 128);
        pdf.setFont('helvetica', 'italic');
        const footerText = `Rapport IA généré automatiquement le ${new Date().toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}`;
        pdf.text(footerText, margin, footerY);
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Powered by OpenAI GPT-4o', pageWidth - margin - 40, footerY);
      };
      
      // Traitement du contenu du rapport
      const lines = report.split('\n').filter(line => line.trim() !== '');
      const totalPages = Math.ceil(lines.length / 35) + 1; // Estimation du nombre de pages
      
      let currentPage = 1;
      let yPos = 40;
      
      // PAGE 1 - En-tête du rapport
      addHeader(currentPage, totalPages);
      
      // Titre principal avec style
      pdf.setFillColor(59, 130, 246);
      pdf.rect(margin, yPos, maxWidth, 12, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RAPPORT D\'ANALYSE FINANCIÈRE IA', margin + 5, yPos + 8);
      yPos += 20;
      
      // Informations sur le rapport
      pdf.setTextColor(26, 26, 26);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const reportInfo = [
        `Période analysée : ${analysisData.analysis.kpis?.currentMonthName || 'N/A'} ${analysisData.analysis.kpis?.yearCurrent || new Date().getFullYear()}`,
        `Date de génération : ${new Date().toLocaleDateString('fr-FR')}`,
        `Source : ${reportSource === 'cache' ? 'Rapport en cache' : 'Généré par IA'}`,
        `Modèle IA : OpenAI GPT-4o`
      ];
      
      reportInfo.forEach((info, index) => {
        pdf.setFillColor(239, 246, 255);
        pdf.rect(margin, yPos - 2, maxWidth, 8, 'F');
        pdf.text(info, margin + 3, yPos + 3);
        yPos += 10;
      });
      
      yPos += 10;
      
      // Traitement du contenu markdown du rapport
      lines.forEach((line) => {
        // Vérifier si on doit changer de page
        if (yPos > pageHeight - 40) {
          addFooter();
          pdf.addPage();
          currentPage++;
          addHeader(currentPage, totalPages);
          yPos = 40;
        }
        
        const cleanLine = line.trim();
        
        if (cleanLine.startsWith('# ')) {
          // Titre principal (H1)
          const title = cleanLine.replace(/^# /, '').replace(/📊|🎯|📈|🔍|🔮|💡|⚠️/g, '').trim();
          
          pdf.setFillColor(16, 185, 129);
          pdf.rect(margin, yPos, maxWidth, 10, 'F');
          
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text(title, margin + 3, yPos + 7);
          yPos += 15;
          
        } else if (cleanLine.startsWith('## ')) {
          // Sous-titre (H2)
          const subtitle = cleanLine.replace(/^## /, '').replace(/📊|🎯|📈|🔍|🔮|💡|⚠️/g, '').trim();
          
          pdf.setFillColor(217, 119, 6);
          pdf.rect(margin, yPos, maxWidth, 8, 'F');
          
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text(subtitle, margin + 3, yPos + 6);
          yPos += 12;
          
        } else if (cleanLine.startsWith('### ')) {
          // Sous-sous-titre (H3)
          const subsubtitle = cleanLine.replace(/^### /, '').replace(/📊|🎯|📈|🔍|🔮|💡|⚠️/g, '').trim();
          
          pdf.setTextColor(59, 130, 246);
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.text(subsubtitle, margin, yPos);
          yPos += 8;
          
        } else if (cleanLine.startsWith('- ') || cleanLine.startsWith('• ')) {
          // Liste à puces
          const listItem = cleanLine.replace(/^[•-] /, '').replace(/\*\*(.*?)\*\*/g, '$1');
          
          pdf.setTextColor(26, 26, 26);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          
          // Ajouter la puce
          pdf.text('•', margin + 3, yPos);
          
          // Texte de la liste avec retour à la ligne automatique
          const splitText = pdf.splitTextToSize(listItem, maxWidth - 10);
          pdf.text(splitText, margin + 8, yPos);
          yPos += splitText.length * 5 + 2;
          
        } else if (cleanLine.startsWith('**') && cleanLine.endsWith('**')) {
          // Texte en gras
          const boldText = cleanLine.replace(/\*\*/g, '');
          
          pdf.setTextColor(26, 26, 26);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text(boldText, margin, yPos);
          yPos += 7;
          
        } else if (cleanLine.length > 0) {
          // Texte normal
          const normalText = cleanLine.replace(/\*\*(.*?)\*\*/g, '$1');
          
          pdf.setTextColor(26, 26, 26);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          
          const splitText = pdf.splitTextToSize(normalText, maxWidth);
          pdf.text(splitText, margin, yPos);
          yPos += splitText.length * 5 + 2;
        } else {
          // Ligne vide - espace
          yPos += 4;
        }
      });
      
      // Ajouter le pied de page sur la dernière page
      addFooter();
      
      // Sauvegarder le PDF
      pdf.save('rapport-analyse-financiere-ia.pdf');
      
      toast.success('PDF exporté avec succès !', {
        duration: 3000,
        icon: '📄',
      });
      
    } catch (error) {
      console.error('Erreur export PDF:', error);
      toast.error('Erreur lors de l\'export PDF', {
        duration: 3000,
      });
    }
  }, [report, reportSource, analysisData]);

  const formatReportForDisplay = (text: string) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-gray-800 mb-4">$1</h1>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-semibold text-gray-700 mb-3 mt-6">$1</h2>')
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-medium text-gray-600 mb-2 mt-4">$1</h3>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 mb-1">• $1</li>')
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>');
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-800">Rapport IA</h1>
          {reportSource === 'cache' && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              En cache
            </span>
          )}
          {reportSource === 'generated' && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              Généré
            </span>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onBackToAnalysis}
            className="btn-secondary space-x-2"
          >
            <span>← Retour à l'analyse</span>
          </button>
          <button
            onClick={onNewAnalysis}
            className="btn-primary space-x-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Nouvelle analyse</span>
          </button>
        </div>
      </div>

      {/* Barre d'actions */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="flex flex-wrap gap-3 mb-4">
          {reportSource === 'cache' && (
            <button
              onClick={() => generateReport(true)}
              disabled={isGenerating}
              className="btn-outline space-x-2"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span>Régénération...</span>
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-5 w-5" />
                  <span>Régénérer Rapport</span>
                </>
              )}
            </button>
          )}
          
          <button
            onClick={copyToClipboard}
            disabled={!report}
            className="btn-secondary space-x-2"
          >
            <ClipboardDocumentIcon className="h-5 w-5" />
            <span>Copier</span>
          </button>
          
          <button
            onClick={exportToPDF}
            disabled={!report}
            className="btn-outline space-x-2"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Export PDF</span>
          </button>
        </div>

        {/* Informations sur le cache */}
        {reportSource === 'cache' && existingReport && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <InformationCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-800 mb-1">Rapport en cache</h3>
                <p className="text-green-700 text-sm mb-2">
                  Ce rapport a été généré le {new Date(existingReport.generatedAt).toLocaleDateString('fr-FR')} à {new Date(existingReport.generatedAt).toLocaleTimeString('fr-FR')}
                </p>
                <p className="text-green-600 text-xs">
                  Période analysée : {existingReport.dataSource.period} • {existingReport.dataSource.monthsAnalyzed} mois
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contenu des onglets */}
      {isGenerating ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-lg shadow-lg p-8 text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Génération du rapport en cours...</h3>
          <p className="text-gray-500">
            {reportSource === 'cache' ? 'Régénération avec IA...' : 'Analyse de vos données financières...'}
          </p>
        </motion.div>
      ) : report ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <DocumentTextIcon className="h-8 w-8" />
              Rapport d'Analyse Financière
            </h2>
            <p className="opacity-90 mt-2">
              Rapport généré {reportSource === 'cache' ? 'depuis le cache' : 'par IA'} • {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>
          
          <div className="p-8">
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: formatReportForDisplay(report) }}
            />
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-lg shadow-lg p-8 text-center"
        >
          <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun rapport disponible</h3>
          <p className="text-gray-500 mb-6">
            Une erreur s'est produite lors de la génération du rapport.
          </p>
          <button
            onClick={() => generateReport(true)}
            className="btn-primary space-x-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Réessayer</span>
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default AIReportGenerator; 